import {
  SiInstagram,
  SiReddit,
  SiYoutube,
} from '@icons-pack/react-simple-icons'
import { ExternalLink } from 'lucide-react'
import {
  DISPLAY_SOURCE_KIND_ORDER,
  DRAWER_SOURCE_KIND_ORDER,
  type ReportSourceKind,
  type ReportSourceLink,
  SOURCE_KIND_LABELS,
} from '~/src/lib/reports/reportSources'
import {
  SEO_REPORT_SOURCE_CLICK_SOURCE,
  toBuyLinkHref,
} from '~/src/lib/withRedirectClickSource'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '~/src/components/ui/drawer'

interface SeoReportSourcesProps {
  sources: ReportSourceLink[]
  productCount: number
  sourcesReviewed?: number | null
  /** Footer-style rendering: smaller heading and body copy. Used for the
   *  inline citation list mounted at the bottom of the article — the drawer
   *  trigger in the header remains the primary UX surface, but this version
   *  is what Google indexes. */
  compact?: boolean
}

interface SeoReportSourcesDrawerProps extends SeoReportSourcesProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const MAX_VISIBLE_SOURCES = 42

// Official and retail sources are shop pages, so they go through /redirect
// like buy links and earn when the shop has a deal. Reviews, Reddit and
// video links stay direct.
const SHOP_SOURCE_KINDS = new Set<ReportSourceKind>(['official', 'retail'])

function sourceLinkProps(source: ReportSourceLink) {
  if (!SHOP_SOURCE_KINDS.has(source.kind)) {
    return { href: source.url, rel: 'nofollow noopener noreferrer' }
  }
  return {
    href: toBuyLinkHref(source.url, SEO_REPORT_SOURCE_CLICK_SOURCE),
    rel: 'sponsored nofollow noopener noreferrer',
  }
}

export function hasSeoReportSources(sources: ReportSourceLink[]): boolean {
  return sources.length > 0
}

function SourceLinkItem({
  source,
  compact,
}: {
  source: ReportSourceLink
  compact?: boolean
}) {
  const extraProducts = source.productNames.length - 1
  const productLabel =
    extraProducts > 0
      ? `${source.productNames[0]} + ${extraProducts} more`
      : source.productNames[0]

  return (
    <li
      className={
        compact
          ? 'min-w-0 text-xs leading-5 text-foreground-tertiary'
          : 'min-w-0 text-sm leading-5 text-ds-text-quaternary'
      }
    >
      <a
        {...sourceLinkProps(source)}
        target='_blank'
        referrerPolicy='no-referrer'
        title={productLabel ? `Used for ${productLabel}` : undefined}
        className='break-words font-medium text-info-700 underline underline-offset-2 hover:text-info-800'
      >
        {source.label}
      </a>{' '}
      <span className='text-[11px] leading-4 text-foreground-tertiary'>
        ({source.domain})
      </span>
    </li>
  )
}

function SourceGroup({
  label,
  sources,
  compact,
}: {
  label: string
  sources: ReportSourceLink[]
  compact?: boolean
}) {
  return (
    <div className='min-w-0'>
      <h3 className='mb-1 text-xs font-semibold uppercase tracking-wide text-foreground-tertiary'>
        {label}
      </h3>
      <ul
        className={
          compact
            ? 'columns-1 gap-x-6 md:columns-3'
            : 'columns-1 gap-x-8 md:columns-2'
        }
      >
        {sources.map((source) => (
          <SourceLinkItem key={source.url} source={source} compact={compact} />
        ))}
      </ul>
    </div>
  )
}

function getFaviconUrl(domain: string): string {
  return `https://t0.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${domain}&size=96`
}

function getSourcePlatform(source: ReportSourceLink) {
  const domain = source.domain.toLowerCase()
  const url = source.url.toLowerCase()

  if (domain.includes('youtube.com') || domain.includes('youtu.be')) {
    return 'youtube'
  }
  if (domain.includes('reddit.com')) return 'reddit'
  if (domain.includes('instagram.com')) return 'instagram'
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube'
  if (url.includes('reddit.com')) return 'reddit'
  if (url.includes('instagram.com')) return 'instagram'
  return 'domain'
}

function SourceAvatar({ source }: { source: ReportSourceLink }) {
  const platform = getSourcePlatform(source)
  const className =
    'flex size-9 shrink-0 items-center justify-center rounded-full border border-border bg-white text-foreground shadow-sm'

  if (platform === 'youtube') {
    return (
      <span className={className} aria-hidden='true'>
        <SiYoutube className='size-4 text-red-600' />
      </span>
    )
  }

  if (platform === 'reddit') {
    return (
      <span className={className} aria-hidden='true'>
        <SiReddit className='size-4 text-orange-600' />
      </span>
    )
  }

  if (platform === 'instagram') {
    return (
      <span className={className} aria-hidden='true'>
        <SiInstagram className='size-4 text-pink-600' />
      </span>
    )
  }

  return (
    <span className={className} aria-hidden='true'>
      <img
        src={getFaviconUrl(source.domain)}
        alt=''
        loading='lazy'
        referrerPolicy='no-referrer'
        className='size-5 rounded-sm'
      />
    </span>
  )
}

function SourceDrawerLinkItem({ source }: { source: ReportSourceLink }) {
  const extraProducts = source.productNames.length - 1
  const productLabel =
    extraProducts > 0
      ? `${source.productNames[0]} + ${extraProducts} more`
      : source.productNames[0]

  // Prefer the product name (most informative — "Brooks Glycerin 22") and fall
  // back to the upstream categorical label ("Official product page", "Reddit
  // thread", etc.) when the source isn't tied to a named product. The domain
  // moves to the subtitle so the row reads as `<product> · <domain>`.
  const primaryText = productLabel ?? source.label

  return (
    <li>
      <a
        {...sourceLinkProps(source)}
        target='_blank'
        referrerPolicy='no-referrer'
        title={source.label}
        className='group flex gap-3 py-3 text-sm'
      >
        <SourceAvatar source={source} />
        <span className='grid min-w-0 flex-1 gap-1'>
          <span className='flex min-w-0 items-start justify-between gap-3'>
            <span className='min-w-0 break-words font-medium leading-5 text-foreground group-hover:text-info-700 group-hover:underline group-hover:underline-offset-2'>
              {primaryText}
            </span>
            <ExternalLink
              aria-hidden='true'
              className='mt-0.5 size-4 shrink-0 text-foreground-tertiary transition-colors group-hover:text-info-700'
              strokeWidth={1.75}
            />
          </span>
          <span className='text-sm leading-5 text-ds-text-quaternary'>
            {source.domain}
          </span>
        </span>
      </a>
    </li>
  )
}

function SourceDrawerGroup({
  label,
  sources,
}: {
  label: string
  sources: ReportSourceLink[]
}) {
  return (
    <section className='min-w-0'>
      <div className='mb-2 flex items-center justify-between gap-3'>
        <h3 className='text-xs font-semibold uppercase tracking-wide text-foreground-tertiary'>
          {label}
        </h3>
        <span className='rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-ds-text-quaternary'>
          {sources.length.toLocaleString()}
        </span>
      </div>
      <ul className='divide-y divide-border'>
        {sources.map((source) => (
          <SourceDrawerLinkItem key={source.url} source={source} />
        ))}
      </ul>
    </section>
  )
}

function getSourceGroups(
  sources: ReportSourceLink[],
  kindOrder: ReportSourceKind[]
) {
  return kindOrder
    .map((kind) => ({
      kind,
      label: SOURCE_KIND_LABELS[kind],
      sources: sources.filter((source) => source.kind === kind),
    }))
    .filter((group) => group.sources.length > 0)
}

export function SeoReportSources({
  sources: allSources,
  productCount,
  sourcesReviewed,
  compact = false,
}: SeoReportSourcesProps) {
  const sources = allSources.filter((source) => source.kind !== 'retail')
  if (sources.length === 0) return null

  const visibleSources = sources.slice(0, MAX_VISIBLE_SOURCES)
  const groups = getSourceGroups(visibleSources, DISPLAY_SOURCE_KIND_ORDER)
  const hiddenCount = Math.max(0, sources.length - visibleSources.length)
  const displayedTotal = sourcesReviewed ?? sources.length

  return (
    <section
      className={
        compact ? 'flex flex-col gap-3 text-xs' : 'flex flex-col gap-4'
      }
    >
      <div className='flex max-w-3xl flex-col gap-1.5'>
        <h2
          className={
            compact
              ? 'text-sm font-semibold uppercase tracking-wide text-foreground-tertiary'
              : 'font-display text-xl font-bold text-foreground'
          }
        >
          Sources reviewed
        </h2>
        <p
          className={
            compact
              ? 'text-xs leading-4 text-foreground-tertiary'
              : 'text-sm leading-5 text-ds-text-quaternary'
          }
        >
          {displayedTotal.toLocaleString()} sources checked across{' '}
          {productCount.toLocaleString()} products. Showing non-retail research
          links from the canonical report payload.
        </p>
      </div>

      <div className='flex flex-col gap-3'>
        {groups.map(({ kind, label, sources: groupSources }) => (
          <SourceGroup
            key={kind}
            label={label}
            sources={groupSources}
            compact={compact}
          />
        ))}
      </div>

      {hiddenCount > 0 && (
        <p
          className={
            compact
              ? 'text-xs leading-4 text-foreground-tertiary'
              : 'text-sm leading-5 text-ds-text-quaternary'
          }
        >
          Showing {visibleSources.length.toLocaleString()} research links;{' '}
          {hiddenCount.toLocaleString()} additional non-retail links remain in
          the source data.
        </p>
      )}
    </section>
  )
}

export function SeoReportSourcesDrawer({
  open,
  onOpenChange,
  sources,
  productCount,
  sourcesReviewed,
}: SeoReportSourcesDrawerProps) {
  if (sources.length === 0) return null

  const groups = getSourceGroups(sources, DRAWER_SOURCE_KIND_ORDER)
  const displayedTotal = sourcesReviewed ?? sources.length

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction='right'
      shouldScaleBackground={false}
    >
      <DrawerContent
        className='fixed inset-y-0 right-0 left-auto z-50 flex h-full w-full max-w-xl flex-col rounded-none bg-white text-foreground md:rounded-l-2xl'
        hideNotch
        showXClose
      >
        <div className='flex h-full flex-col overflow-hidden'>
          <div className='border-b border-border px-6 pb-5 pr-14 pt-6'>
            <DrawerTitle className='text-xl font-bold text-foreground'>
              Sources reviewed
            </DrawerTitle>
            <DrawerDescription className='mt-2 text-sm leading-5 text-ds-text-quaternary opacity-100'>
              {displayedTotal.toLocaleString()} sources checked across{' '}
              {productCount.toLocaleString()} products.
            </DrawerDescription>
            <div className='mt-4 grid grid-cols-2 gap-2'>
              <div className='rounded-lg border border-border px-3 py-2'>
                <p className='text-lg font-semibold leading-6 text-foreground'>
                  {productCount.toLocaleString()}
                </p>
                <p className='text-xs text-ds-text-quaternary'>Products</p>
              </div>
              <div className='rounded-lg border border-border px-3 py-2'>
                <p className='text-lg font-semibold leading-6 text-foreground'>
                  {displayedTotal.toLocaleString()}
                </p>
                <p className='text-xs text-ds-text-quaternary'>Sources</p>
              </div>
            </div>
          </div>

          <div data-vaul-no-drag className='flex-1 overflow-y-auto px-6 py-5'>
            <div className='flex flex-col gap-7'>
              {groups.map(({ kind, label, sources: groupSources }) => (
                <SourceDrawerGroup
                  key={kind}
                  label={label}
                  sources={groupSources}
                />
              ))}
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
